import type Phaser from "phaser";
import { composeDirection, type Direction } from "odyssey-shared";
import type { GameInput, GameKeyboard } from "../ui/input/router.ts";
import type { HeldKeys } from "./controls/held.ts";
import type { PressHandler } from "./controls/keyed-actions.ts";
import type { KeyTable } from "./controls/page.ts";
import { holdMoveKey } from "./controls/press.ts";

/**
 * The scene's keyboard under the input router (ADR-061 decision 4, CLIENT.md section 7.3).
 */

/**
 * The scene's Phaser keyboard as the input router drives it. Switching the game's keyboard manager off
 * stops both its reading and its captures, so the arrows, space and letters it would prevent reach a
 * focused text field; a manager switched off queues nothing, so the release of a key held as the field took
 * focus never arrives, and the scene's keys and the physical keys Move reads are reset instead.
 *
 * @param scene The scene whose keys are read.
 * @param held The physical keys Move reads, let go with the scene's own.
 * @returns The keyboard.
 */
function phaserKeyboard(scene: Phaser.Scene, held: HeldKeys): GameKeyboard {
  return {
    /**
     * Switches the game's keyboard manager on or off.
     *
     * @param enabled True while the game owns the keyboard.
     */
    setEnabled(enabled: boolean): void {
      const manager = scene.game.input.keyboard;
      if (manager !== null) {
        manager.enabled = enabled;
      }
    },
    /**
     * Resets every key of the scene to up.
     */
    release(): void {
      scene.input.keyboard?.resetKeys();
      held.release();
    },
  };
}

/**
 * Hands the scene's keyboard to the router and runs the scene's own key actions on the keys the router lets
 * through, until the scene shuts down. A press's action is the one the key table names for it (`controls/`: the action
 * on the press's physical key, or on the key it is an alias of); its handler consumes the key unless it
 * answers false: it did not take the key, which then does what it would have done (Tab while a control has focus
 * moves focus on).
 *
 * @param scene The scene.
 * @param input The router.
 * @param handlerOf The handler a press runs, or undefined when it runs none.
 * @param held The physical keys Move reads, let go whenever the router hands the keyboard elsewhere.
 */
export function bindGameKeys(scene: Phaser.Scene, input: GameInput, handlerOf: PressHandler, held: HeldKeys): void {
  const unbind = input.bindKeyboard(phaserKeyboard(scene, held));
  const stop = input.onKey((event) => {
    const action = handlerOf(event);
    if (action !== undefined && action(event) !== false) {
      event.preventDefault();
    }
  });
  scene.events.once("shutdown", () => {
    unbind();
    stop();
  });
}

/**
 * The game's event when the window loses focus (`Phaser.Core.Events.BLUR`), named here so this module loads without
 * Phaser itself.
 */
const BLUR = "blur";

/**
 * Reads the movement intent (ADR-061's amendments "Move joins the key table", points 1 and 8, and "every key rebindable
 * but Escape", point 3): Move's two sets of four directions by the physical keys the key table binds them to, held as
 * the router's key watcher hears them. Both axes compose into a diagonal, opposite keys cancel. Every physical key is
 * let go when the router hands the keyboard elsewhere (`bindGameKeys`) and when the window loses focus. The scene's
 * cursor keys are still registered for what they capture, so the arrows and Space never scroll the page, and are never
 * read. Reading allocates nothing.
 *
 * @param scene The scene whose keys are read; its keyboard plugin exists from `create` on.
 * @param input The router, whose watcher hears every key go down and up.
 * @param keys The key table.
 * @param held The physical keys held, shared with `bindGameKeys` and Sprint.
 * @returns A function that reads the direction the keys hold now.
 */
export function movementKeys(scene: Phaser.Scene, input: GameInput, keys: Pick<KeyTable, "actionOf">, held: HeldKeys): () => Direction {
  scene.input.keyboard?.createCursorKeys();
  const unwatch = input.watchKeys((event, down, owned) => holdMoveKey(held, event, down, owned, (press) => input.takenElsewhere(press)));
  const lost = (): void => held.release();
  scene.game.events.on(BLUR, lost);
  scene.events.once("shutdown", () => {
    unwatch();
    scene.game.events.off(BLUR, lost);
    held.release();
  });
  return () =>
    composeDirection(
      held.heldAs(keys, "moveUp") || held.heldAs(keys, "moveUpAlt"),
      held.heldAs(keys, "moveDown") || held.heldAs(keys, "moveDownAlt"),
      held.heldAs(keys, "moveLeft") || held.heldAs(keys, "moveLeftAlt"),
      held.heldAs(keys, "moveRight") || held.heldAs(keys, "moveRightAlt"),
    );
}
